import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMissingSameDayProviderVisitsCount, useMissingSameDayProviderVisitsData, invalidateAllWidgetQueries } from '../../../hooks/useWidgetData';
import { useSelectedDashboardFacility } from '../../../hooks/useSelectedDashboardFacility';
import { toast } from '@rickylandino/react-messages';
import { CheckCircle, ExternalLink } from 'lucide-react';
import { Table, Modal } from 'antd';
import Globals from '../../../config/globals';
import { updateSameDayProviderVisit } from '../../../common/services/next-gen/ExamService';
import WidgetCountState from '../WidgetCountState';
import WidgetDataError from '../WidgetDataError';

/**
 * MissingSameDayProviderVisitsWidget component
 * Shows all exams missing a same-day provider visit with server-side pagination
 * @param {Object} props
 * @param {boolean} [props.isCollapsed=false] - Whether the widget is collapsed
 * @param {boolean} [props.isExpanded=false] - Whether the widget is in fullscreen mode
 * @param {Function} [props.setIsLoading] - Callback to set loading state for parent
 * @param {Function} [props.setSharedState] - Callback to update shared state (including refetch)
 * @param {boolean} [props.queryEnabled=true] - Whether queries are enabled (stream gate)
 */
export default function MissingSameDayProviderVisitsWidget({ isCollapsed = false, isExpanded = false, setIsLoading, setSharedState, queryEnabled = true }) {
const queryClient = useQueryClient();
const { selectedFacilities } = useSelectedDashboardFacility();

// Pagination state
const [currentPage, setCurrentPage] = useState(1);
const [pageSize, setPageSize] = useState(50);

// Update modal state
const [showModal, setShowModal] = useState(false);
const [selectedRow, setSelectedRow] = useState(null);
const [sameDayValue, setSameDayValue] = useState(null);
const [saving, setSaving] = useState(false);

    const shouldLoadFullData = !isCollapsed || isExpanded;

    const { data: countData, isLoading: isLoadingCount, isError: isCountError, refetch: refetchCount } = useMissingSameDayProviderVisitsCount((isCollapsed && !isExpanded) && queryEnabled, selectedFacilities);

    const {
        data,
        isLoading,
        isFetching,
        isError,
        error,
        refetch
    } = useMissingSameDayProviderVisitsData(currentPage, pageSize, shouldLoadFullData, selectedFacilities);

    const results = data?.data || [];
    const totalCount = data?.totalCount || 0;
    const count = shouldLoadFullData ? totalCount : (countData?.count || 0);

    useEffect(() => {
        if (setIsLoading && isExpanded) {
            setIsLoading(isLoading || isFetching);
        }
    }, [isLoading, isFetching, isExpanded, setIsLoading]);

    useEffect(() => {
        if (shouldLoadFullData && isError) {
            console.error('Failed to load missing same-day provider visits:', error);
            toast.error(`Failed to load missing same-day provider visits: ${error?.message || 'Unknown error'}`);
        }
    }, [shouldLoadFullData, isError, error]);

    useEffect(() => {
        if (setSharedState && refetch) {
            setSharedState(prev => ({ ...prev, refetch }));
        }
    }, [setSharedState, refetch]);

    const formatMRN = useCallback((mrn) => {
        if (!mrn) return '';
        return mrn.replace(/(.{10})/g, '$1 ').trim();
    }, []);

    const formatDate = useCallback((dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric'
        });
    }, []);

    const sortedResults = useMemo(() => {
        if (!results || results.length === 0) return [];
        return [...results].sort((a, b) => new Date(a.examDateTime).getTime() - new Date(b.examDateTime).getTime());
    }, [results]);

    const handleOpenModal = useCallback((row) => {
        setSelectedRow(row);
        setSameDayValue(null);
        setShowModal(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setShowModal(false);
        setSelectedRow(null);
        setSameDayValue(null);
    }, []);

    const handleSave = useCallback(async () => {
        if (!selectedRow?.patientExamID) {
            toast.error('Missing exam information.');
            return;
        }
        if (sameDayValue === null) {
            toast.error('Please select a value before saving.');
            return;
        }

        setSaving(true);
        try {
            await updateSameDayProviderVisit(selectedRow.patientExamID, sameDayValue === 'Yes');
            toast.success('Same-day provider visit updated.');
            handleCloseModal();
            invalidateAllWidgetQueries(queryClient);
        } catch (e) {
            console.error('Failed to update same-day provider visit:', e);
            toast.error('Failed to save. Please try again.');
        } finally {
            setSaving(false);
        }
    }, [selectedRow, sameDayValue, queryClient, handleCloseModal]);

    const columns = useMemo(() => [
        {
            title: 'Patient Name',
            dataIndex: 'patientName',
            key: 'patientName',
            width: 200,
        },
        {
            title: 'MRN',
            dataIndex: 'mrn',
            key: 'mrn',
            render: (mrn) => <span style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>{formatMRN(mrn)}</span>,
            width: 150,
        },
        {
            title: 'Facility',
            dataIndex: 'facilityName',
            key: 'facilityName',
            width: 180,
        },
        {
            title: 'Scheduled Date',
            dataIndex: 'examDateTime',
            key: 'examDateTime',
            render: (examDateTime) => formatDate(examDateTime),
            width: 140,
        },
        {
            title: 'Exam Type',
            dataIndex: 'examType',
            key: 'examType',
            width: 150,
        },
        {
            title: 'Actions',
            key: 'action',
            width: 100,
            fixed: 'right',
            render: (_, row) => (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleOpenModal(row);
                    }}
                    className="px-3 py-1.5 rounded-lg font-medium text-sm transition-colors inline-flex items-center gap-1"
                    style={{
                        backgroundColor: Globals.theme === 'dark' ? '#ec4899' : '#db2777',
                        color: 'white',
                        border: 'none',
                        cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor =
                            Globals.theme === 'dark' ? '#db2777' : '#be185d';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor =
                            Globals.theme === 'dark' ? '#ec4899' : '#db2777';
                    }}
                >
                    Update
                    <ExternalLink size={14} />
                </button>
            )
        }
    ], [formatMRN, formatDate, handleOpenModal]);

    const getCountColor = () => Globals.theme === 'dark' ? '#ec4899' : '#db2777';

    const getCountMessage = () => count === 1 ? 'missing same-day provider visit' : 'missing same-day provider visits';

    // COLLAPSED STATE � show only count
    if (isCollapsed && !isExpanded) {
        return (
            <WidgetCountState
                isLoading={isLoadingCount}
                isError={isCountError}
                hasData={countData !== undefined}
                onRetry={refetchCount}
                mutedColor={Globals.theme === 'dark' ? '#a1a1aa' : '#71717a'}
            >
                <div
                    className="text-5xl font-light mb-2"
                    style={{ color: getCountColor() }}
                >
                    {count.toLocaleString()}
                </div>
                <div
                    className="text-sm"
                    style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}
                >
                    {getCountMessage()}
                </div>
            </WidgetCountState>
        );
    }

    // Data query failed - never fall through to the empty state
    if (isError) {
        return (
            <WidgetDataError
                onRetry={refetch}
                mutedColor={Globals.theme === 'dark' ? '#a1a1aa' : '#71717a'}
            />
        );
    }

    // Empty state
    if (!isLoading && count === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-4">
                <CheckCircle
                    className="h-16 w-16 mx-auto mb-4"
                    style={{ color: getCountColor() }}
                />
                <p style={{ color: getCountColor() }}>
                    No Missing Same-Day Provider Visits Found
                </p>
            </div>
        );
    }

    // EXPANDED STATE � table with server-side pagination
    return (
        <>
            <div className="space-y-4">
                <Table
                    columns={columns}
                    dataSource={sortedResults}
                    rowKey={(record, index) => `${record.patientName}-${record.examDateTime}-${record.mrn}-${record.examType}-${index}`}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalCount,
                        onChange: (page, newPageSize) => {
                            setCurrentPage(page);
                            if (newPageSize !== pageSize) {
                                setPageSize(newPageSize);
                                setCurrentPage(1);
                            }
                        },
                        pageSizeOptions: ['25', '50', '100', '200'],
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} visits`,
                    }}
                    size="small"
                    scroll={{ y: 500 }}
                    virtual
                    loading={(isLoading && !isExpanded) || isFetching}
                />
            </div>

            <Modal
                visible={showModal}
                title="Update Same-Day Provider Visit"
                onCancel={handleCloseModal}
                onOk={handleSave}
                okText="Save"
                cancelText="Cancel"
                confirmLoading={saving}
                okButtonProps={{
                    style: {
                        backgroundColor: Globals.theme === 'dark' ? '#ec4899' : '#db2777',
                        borderColor: Globals.theme === 'dark' ? '#ec4899' : '#db2777',
                    }
                }}
                destroyOnClose
            >
                {selectedRow && (
                    <div className="space-y-4 py-2">
                        <div className="text-sm" style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}>
                            <div><span className="font-medium">Patient:</span> {selectedRow.patientName}</div>
                            <div><span className="font-medium">MRN:</span> {formatMRN(selectedRow.mrn)}</div>
                            <div><span className="font-medium">Exam Date:</span> {formatDate(selectedRow.examDateTime)}</div>
                            <div><span className="font-medium">Exam Type:</span> {selectedRow.examType}</div>
                            <div><span className="font-medium">Facility:</span> {selectedRow.facilityName}</div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Same-Day Provider Visit
                            </label>
                            <select
                                value={sameDayValue ?? ''}
                                onChange={(e) => setSameDayValue(e.target.value || null)}
                                className="w-full border rounded px-3 py-2 text-sm"
                                style={{
                                    borderColor: Globals.theme === 'dark' ? '#52525b' : '#d4d4d8',
                                    backgroundColor: Globals.theme === 'dark' ? '#27272a' : '#ffffff',
                                    color: Globals.theme === 'dark' ? '#f4f4f5' : '#18181b',
                                }}
                            >
                                <option value="">-- Select --</option>
                                <option value="Yes">Yes</option>
                                <option value="No">No</option>
                            </select>
                        </div>
                    </div>
                )}
            </Modal>
        </>
    );
}
